import { useState, useEffect, useCallback } from "react";
import { useToast } from '../../../../shared/toastMessages/toastProvider';
import { Typography, TextField, Stack, Paper } from '@mui/material';

import TableComponent from "../../../../shared/table/table";
import getApiData from '../../../../../hooks/apiRequests/getRequest';
import postApiData from '../../../../../hooks/apiRequests/postsRequest';
import putApiData from '../../../../../hooks/apiRequests/putRequest';
import deleteApiData from "../../../../../hooks/apiRequests/deleteRequest";
import ModalComponent from "../../../../shared/modal/modal";
import { normalizePageParam } from "../../../../../hooks/pagination";

import { theme } from '../../../../../configs/theme';

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

const SpamList = () => {
  const [data, setData] = useState();
  const [progress, setProgress] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState("1");

  const { showToast } = useToast();

  const fetchSpamList = useCallback(async () => {
    setProgress(true);
    try {
      const response = await getApiData(
        `/Spam/List?Search_filter=${encodeURIComponent(query)}&page_number=${page}`
      );
      if (!response?.data) {
        throw new Error("Empty spam list response");
      }
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("Failed to load data", "error");
    } finally {
      setProgress(false);
    }
  }, [query, page, showToast]);

  useEffect(() => {
    fetchSpamList();
  }, [fetchSpamList]);

  const newpage = async (nextPage, search) => {
    setQuery(search || "");
    setPage(normalizePageParam(nextPage));
  };

  const addSpam = async () => {
    const trimmed = phrase.trim();
    if (!trimmed) {
      showToast("Please enter a spam phrase", "error");
      return;
    }

    const body = { id: 0, phrase: trimmed, time_created: "" };

    try {
      const response = await postApiData("/Create/Spam", body);
      if (response?.error || !response?.data) {
        showToast("Failed to add spam phrase", "error");
        return;
      }
      showToast("Added new spam phrase successfully", "success");
      setOpenAdd(false);
      setPhrase("");
      setPage("1");
      await fetchSpamList();
    } catch (error) {
      console.error("Error adding spam phrase:", error);
      showToast("Unable to add spam phrase", "error");
    }
  };

  const editSpam = async () => {
    const trimmed = phrase.trim();
    if (!trimmed) {
      showToast("Please enter a spam phrase", "error");
      return;
    }

    const body = { id: selectedId, phrase: trimmed };

    try {
      const response = await putApiData("/Update/Spam", body);
      if (response?.data) {
        showToast("Updated spam phrase successfully", "success");
        setOpenEdit(false);
        setPhrase("");
        setSelectedId(null);
        await fetchSpamList();
      } else {
        showToast("Failed to update spam phrase", "error");
      }
    } catch (error) {
      console.error("Error updating spam phrase:", error);
      showToast("Unable to update spam phrase", "error");
    }
  };

  const DeleteSpam = async () => {
    try {
      const response = await deleteApiData(`/Delete/Spam?SpamId=${selectedId}`);
      if (!response) {
        showToast("Failed to delete spam phrase", "error");
        return;
      }
      showToast("Spam phrase deleted successfully", "success");
      setOpenDelete(false);
      setSelectedId(null);
      await fetchSpamList();
    } catch (error) {
      console.error("Error deleting spam phrase:", error);
      showToast("Unable to delete spam phrase", "error");
    }
  };

  const handleAdd = () => {
    if (openAdd) {
      setOpenAdd(false);
      setPhrase("");
      return;
    }
    setPhrase("");
    setOpenAdd(true);
  };

  const handleEdit = (id) => {
    const item = data?.data.find((x) => x.id === id);
    setSelectedId(id);
    setPhrase(item?.phrase || "");
    setOpenEdit(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDelete(true);
  };

  return (
    <>
      <Paper elevation={0} sx={cardSx}>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Spam list
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Words and phrases used to filter unwanted email content.
          </Typography>
        </Stack>

        <TableComponent
          apiData={data}
          progress={progress}
          noResults={!progress && Array.isArray(data?.data) && data.data.length === 0}
          columnNames={{
            phrase: "Words/Phrase",
            time_updated: "Time Updated",
          }}
          handleAdd={handleAdd}
          handleOpen={handleEdit}
          handleSpecDelete={handleDeleteClick}
          theme={theme}
          newpage={newpage}
          query={query}
          pageNumber={page}
        />
      </Paper>

      <ModalComponent
        title={"Add New Spam Phrase"}
        subtitle="Add a word or phrase used to filter unwanted content."
        open={openAdd}
        handleOpen={handleAdd}
        theme={theme}
        handleSubmit={addSpam}
        alternateSaveMessage="Add phrase"
      >
        <TextField
          label="Phrase"
          variant="outlined"
          fullWidth
          size="small"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </ModalComponent>

      <ModalComponent
        title={"Edit Spam Phrase"}
        subtitle="Update this spam filter phrase."
        open={openEdit}
        handleOpen={() => setOpenEdit(false)}
        theme={theme}
        handleSubmit={editSpam}
        alternateSaveMessage="Save changes"
      >
        <TextField
          label="Phrase"
          variant="outlined"
          fullWidth
          size="small"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </ModalComponent>

      <ModalComponent
        title={"Confirm Delete"}
        subtitle="This will permanently remove the spam phrase."
        open={openDelete}
        handleOpen={() => setOpenDelete(false)}
        theme={theme}
        handleSubmit={DeleteSpam}
        alternateSaveMessage="Delete phrase"
        submitColor="error"
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete this spam phrase?
        </Typography>
      </ModalComponent>
    </>
  );
};

export default SpamList;
